import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClientBasicInformation from '../../components/client/ClientBasicInformation'
import { getEmptyClientObject } from '../../utils/client-utils'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { ClientManager } from '../../services/api/ClientManager'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { ClientDto, ClientType } from '../../services/api/sharedTypes/Client'
import { RouteConstants } from '../../navigation/navigation-types'
import { setSaveState } from '../../services/redux/savestate/savestate-actions'
import CustomContainer from '../../components/common/CustomContainer'
import DuplicateClientDialog from '../../components/client/DuplicateClientDialog'
import BottomBar from '../../components/common/BottomBar'

const AddClient: React.FC = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const location = useLocation()

    const params = new URLSearchParams(location.search)

    const [isClientDuplicateDialogOpen, setIsClientDuplicateDialogOpen] = useState<boolean>(false)
    const [duplicates, setDuplicates] = useState<string[]>([])
    const [saveType, setSaveType] = useState<'save' | 'saveAndAdd'>('save')
    const [isClientSaving, setIsClientSaving] = useState<boolean>(false)
    const [resetKey, setResetKey] = useState<number>(0)
    const [client, setClient] = useState<ClientDto>({
        ...getEmptyClientObject(),
        name: params.get('name') || '',
        contacts: [
            {
                name: params.get('contact') || '',
                email: params.get('email') || '',
                phone: params.get('phone') || '',
            },
        ],
    })

    const canGoBack =
        client.name ||
            client.address.city ||
            client.address.street ||
            client.address.zip ||
            client.valueAddedTaxIdentificationNumber ||
            client.companyRegistrationNumber ||
            client.clientType !== ClientType.Citizen ||
            client.contacts.length > 1 ||
            client.notifications.length > 0 ||
            client.contacts[0].name ||
            client.contacts[0].email ||
            client.contacts[0].phone ||
            client.note
            ? false
            : true

    useEffect(() => {
        dispatch(setSaveState(canGoBack))
    }, [canGoBack, dispatch])

    const handleCloseDialog = () => {
        setIsClientDuplicateDialogOpen(false)
        setDuplicates([])
    }

    const saveClient = async () => {
        setIsClientSaving(true)
        setDuplicates([])
        const clientManager = ClientManager.getManager()
        try {
            const clientRes = await clientManager.createClientAsync(client)
            if (clientRes) setClient(clientRes)
        } catch (error) {
            setIsClientSaving(false)
            dispatch(displayMessage({ message: error, type: 'error' }))
            return
        }
        setIsClientSaving(false)

        location.key === 'default' ? navigate(RouteConstants.clients) : navigate(-1)
        dispatch(displayMessage({ message: `Klient byl uložen`, type: 'success' }))
    }

    const validateClientUniqueness = async (saveType: 'save' | 'saveAndAdd') => {
        setIsClientSaving(true)
        setDuplicates([])
        setSaveType(saveType)

        const clientManager = ClientManager.getManager()
        try {
            const uniqueness = await clientManager.validateClientUniquenessAsync({
                name: client.name,
                companyRegistrationNumber: client.companyRegistrationNumber,
                contacts: client.contacts,
            }
            )
            if (uniqueness.isUnique) {
                saveType === 'save' ? saveClient() : saveClientAndAddNew()
            } else {
                setDuplicates(uniqueness.conflictedProperties)
                setIsClientDuplicateDialogOpen(true)
            }
        } catch (error) {
            setIsClientSaving(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setIsClientSaving(false)
    }

    const saveClientAndAddNew = async () => {
        setIsClientSaving(true)
        setDuplicates([])
        const clientManager = ClientManager.getManager()
        try {
            const clientRes = await clientManager.createClientAsync(client)
            if (clientRes) {
                setClient(getEmptyClientObject())
                setResetKey((prevKey) => prevKey + 1)
            }
        } catch (error) {
            setIsClientSaving(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setIsClientSaving(false)

        dispatch(displayMessage({ message: `Klient byl uložen`, type: 'success' }))
    }

    return (
        <CustomContainer>
            <ClientBasicInformation client={client} onChange={setClient} resetKey={resetKey} />

            <BottomBar onSave={() => validateClientUniqueness('save')} onSaveAndAdd={() => validateClientUniqueness('saveAndAdd')} defaultRoute={RouteConstants.clients} disabled={isClientSaving} />

            <DuplicateClientDialog dialogOpen={isClientDuplicateDialogOpen} handleCloseDialog={handleCloseDialog} handleSave={saveType === 'saveAndAdd' ? () => saveClientAndAddNew() : () => saveClient()} duplicates={duplicates} />
        </CustomContainer>
    )
}

export default AddClient
